import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { UserProvider, useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { SubscriptionProvider } from "@foundation/altzero_base/base/contextapi/SubscriptionContext";
import {
  PluginProvider,
  PluginRoutes,
  PluginProviders,
} from "./plugins/loader";
import { Toaster } from "@foundation/altzero_base/base/components/ui/toaster";
import { supabase, setupSessionRefresh } from "@foundation/altzero_base/base/utils/supabaseClient";
import { useUserRole } from "@foundation/altzero_base/base/hooks/useUserRole";
import MainLayout from "./layout/MainLayout";
import Login from "@foundation/altzero_base/base/components/auth/Login";
import Signup from "@foundation/altzero_base/base/components/auth/Signup";
import Profile from "@foundation/altzero_base/base/pages/EnhancedProfileSettings";
import ForgotPassword from "@foundation/altzero_base/base/pages/forgot-password";
import UpdatePassword from "@foundation/altzero_base/base/pages/update-password";
import Settings from "@foundation/altzero_base/base/pages/Settings";
import AdminPortal from "@foundation/altzero_base/base/pages/AdminPortal";
import { AdminProtectedRoute } from "@foundation/altzero_base/base/components/admin/AdminProtectedRoute";
import PendingApprovalPage from "@foundation/altzero_base/base/components/auth/PendingApprovalPage";
import { AnonymousSessionProvider } from "./contexts/AnonymousSessionContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { QREntry } from "./pages/QREntry";
import { WelcomePage } from "./pages/WelcomePage";
import { AssessmentPage } from "./features/sideline-assessment/pages/AssessmentPage";
import { VenueManagementPage } from "./features/venue-management/pages/VenueManagementPage";
import { AssessmentHistoryPage } from "./features/assessment-history/pages/AssessmentHistoryPage";
import { DashboardPage } from "./features/dashboard/pages/DashboardPage";
import { ApiKeysPage } from "./features/api-keys/pages/ApiKeysPage";
import { useAutoOnboarding } from "./hooks/useAutoOnboarding";

// Redirect /venue/:venueId → /qr/:venueId
const VenueRedirect = () => {
  const { venueId } = useParams<{ venueId: string }>();
  return <Navigate to={`/qr/${venueId}`} replace />;
};

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="dark:text-white/60 text-gray-600 mb-4">
              The application encountered an error. Please try refreshing the
              page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-sy-teal text-sy-navy rounded hover:bg-sy-teal/90 font-medium"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Root redirect component
const RootRedirect = () => {
  const { user, isLoading } = useUser();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sy-teal"></div>
      </div>
    );
  }

  try {
    if (user) {
      return <Navigate to="/home" replace />;
    } else {
      return <Navigate to="/welcome" replace />;
    }
  } catch (error) {
    console.error("Error in root redirect:", error);
    return <Navigate to="/welcome" replace />;
  }
};

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useUser();
  const { canAccessApp, needsApproval, isLoading: roleLoading } = useUserRole();
  const { isReady: isOnboardingReady, state: onboardingState, error: onboardingError } = useAutoOnboarding();

  if (onboardingError) {
    console.warn('Auto-onboarding encountered an error:', onboardingError);
  }

  if (isLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sy-teal"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  if (needsApproval) {
    return <PendingApprovalPage />;
  }

  if (!canAccessApp) {
    return <Navigate to="/welcome" replace />;
  }

  if (!isOnboardingReady && (onboardingState === 'onboarding' || onboardingState === 'checking')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sy-teal"></div>
        <p className="text-sm dark:text-white/60 text-gray-500 animate-pulse">
          Setting up your account...
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

// Authenticated routes wrapped in MainLayout
const AuthenticatedRoutes = () => {
  const { user, isLoading } = useUser();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const initializeSession = async () => {
      try {
        cleanup = await setupSessionRefresh();
        const { data } = await supabase.auth.getSession();
        console.log("Current session:", data.session ? "Active" : "None");
      } catch (error) {
        console.error("Session initialization failed:", error);
      }
    };

    initializeSession();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sy-teal"></div>
      </div>
    );
  }

  return (
    <MainLayout>
      <Routes>
        {/* Public auth routes (still show header via MainLayout) */}
        <Route
          path="/login"
          element={user ? <Navigate to="/home" replace /> : <Login />}
        />
        <Route
          path="/signup"
          element={user ? <Navigate to="/home" replace /> : <Signup />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Dashboard redirects to home — non-admin users land here from AdminProtectedRoute */}
        <Route
          path="/dashboard"
          element={<Navigate to="/home" replace />}
        />

        {/* Protected routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/api-keys"
          element={
            <ProtectedRoute>
              <ApiKeysPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <AdminPortal />
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/venues"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <VenueManagementPage />
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessments"
          element={
            <ProtectedRoute>
              <AssessmentHistoryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <DashboardPage />
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Handle root route */}
        <Route path="/" element={<RootRedirect />} />

        {/* Plugin Routes — includes /home, /chat, /assessment for logged-in users */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <PluginRoutes />
            </ProtectedRoute>
          }
        />
      </Routes>
    </MainLayout>
  );
};

// Top-level routes — public routes bypass MainLayout entirely
const AppRoutes = () => {
  return (
    <Routes>
      {/* QR code entry — full-screen, no header, no auth */}
      <Route
        path="/qr/:venueId"
        element={
          <AnonymousSessionProvider>
            <QREntry />
          </AnonymousSessionProvider>
        }
      />

      {/* /venue/:slug redirects to /qr/:slug for URL compatibility */}
      <Route path="/venue/:venueId" element={<VenueRedirect />} />

      {/* Welcome page — full-screen, no header, no auth (default landing) */}
      <Route
        path="/welcome"
        element={<WelcomePage />}
      />

      {/* Public assessment — no auth, no header (for QR → assessment flow) */}
      <Route
        path="/assessment/public"
        element={
          <AnonymousSessionProvider>
            <AssessmentPage />
          </AnonymousSessionProvider>
        }
      />

      {/* Org-specific assessment — slug resolves org context (e.g. /assessment/judo → Judo Australia) */}
      <Route
        path="/assessment/:orgSlug"
        element={
          <AnonymousSessionProvider>
            <AssessmentPage />
          </AnonymousSessionProvider>
        }
      />

      {/* Everything else goes through MainLayout + auth */}
      <Route path="/*" element={<AuthenticatedRoutes />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider>
    <ErrorBoundary>
      <BrowserRouter>
        <UserProvider>
          <SubscriptionProvider>
            <PluginProvider>
              <PluginProviders>
                <div className="relative min-h-screen">
                  <AppRoutes />
                  <Toaster />
                </div>
              </PluginProviders>
            </PluginProvider>
          </SubscriptionProvider>
        </UserProvider>
      </BrowserRouter>
    </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
